import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Lock as LockIcon,
  LockOpen as LockOpenIcon, CheckCircle as CheckCircleIcon, AutoAwesome as AutoAwesomeIcon,
  MoreVert as MoreVertIcon, Transform as TransformIcon, DeleteForever as DeleteForeverIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { fiscalPeriodsService } from '../services/fiscalPeriods.service';
import { FiscalPeriodDto, CreateFiscalPeriodDto } from '../types/fiscalPeriods.types';
import FiscalPeriodDialog from '../components/fiscalPeriods/FiscalPeriodDialog';
import GeneratePeriodsDialog from '../components/fiscalPeriods/GeneratePeriodsDialog';

const FiscalPeriodsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [periods, setPeriods] = useState<FiscalPeriodDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<FiscalPeriodDto | null>(null);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<FiscalPeriodDto | null>(null);

  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [periodToConvert, setPeriodToConvert] = useState<FiscalPeriodDto | null>(null);

  const [deleteDbDialogOpen, setDeleteDbDialogOpen] = useState(false);
  const [periodToDeleteDb, setPeriodToDeleteDb] = useState<FiscalPeriodDto | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPeriod, setMenuPeriod] = useState<FiscalPeriodDto | null>(null);

  useEffect(() => { loadPeriods(); }, []);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const data = await fiscalPeriodsService.getAll();
      setPeriods(data);
    } catch {
      toast.error(isRTL ? 'فشل تحميل الفترات المالية' : 'Failed to load fiscal periods');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, period: FiscalPeriodDto) => {
    setAnchorEl(event.currentTarget);
    setMenuPeriod(period);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPeriod(null);
  };

  const handleAdd = () => {
    setSelectedPeriod(null);
    setDialogOpen(true);
  };

  const handleEdit = (period: FiscalPeriodDto) => {
    setSelectedPeriod(period);
    setDialogOpen(true);
  };

  const handleSave = async (data: CreateFiscalPeriodDto) => {
    if (selectedPeriod) {
      await fiscalPeriodsService.update(selectedPeriod.fiscalPeriodId, { ...data, fiscalPeriodId: selectedPeriod.fiscalPeriodId });
      toast.success(isRTL ? 'تم تحديث الفترة بنجاح' : 'Period updated successfully');
    } else {
      await fiscalPeriodsService.create(data);
      toast.success(isRTL ? 'تم إضافة الفترة بنجاح' : 'Period created successfully');
    }
    await loadPeriods();
  };

  const handleDeleteClick = (period: FiscalPeriodDto) => {
    setPeriodToDelete(period);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!periodToDelete) return;
    try {
      await fiscalPeriodsService.delete(periodToDelete.fiscalPeriodId);
      toast.success(isRTL ? 'تم حذف الفترة بنجاح' : 'Period deleted successfully');
      await loadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل حذف الفترة' : 'Failed to delete period'));
    } finally {
      setDeleteDialogOpen(false);
      setPeriodToDelete(null);
    }
  };

  const handleClose = async (period: FiscalPeriodDto) => {
    try {
      await fiscalPeriodsService.close(period.fiscalPeriodId);
      toast.success(isRTL ? 'تم إغلاق الفترة بنجاح' : 'Period closed successfully');
      await loadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل إغلاق الفترة' : 'Failed to close period'));
    }
  };

  const handleOpen = async (period: FiscalPeriodDto) => {
    try {
      await fiscalPeriodsService.open(period.fiscalPeriodId);
      toast.success(isRTL ? 'تم فتح الفترة بنجاح' : 'Period opened successfully');
      await loadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل فتح الفترة' : 'Failed to open period'));
    }
  };

  const handleSetCurrent = async (period: FiscalPeriodDto) => {
    try {
      await fiscalPeriodsService.setCurrent(period.fiscalPeriodId);
      toast.success(isRTL ? 'تم تعيين الفترة كحالية بنجاح' : 'Period set as current successfully');
      await loadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل تعيين الفترة' : 'Failed to set current period'));
    }
  };

  const handleGenerate = async (year: number, periodType: string) => {
    await fiscalPeriodsService.generate({ year, periodType });
    toast.success(isRTL ? 'تم توليد الفترات بنجاح' : 'Periods generated successfully');
    await loadPeriods();
  };

  const handleConvertToMainClick = (period: FiscalPeriodDto) => {
    setPeriodToConvert(period);
    setConvertDialogOpen(true);
    handleMenuClose();
  };

  const handleConvertToMainConfirm = async () => {
    if (!periodToConvert) return;
    try {
      const result = await fiscalPeriodsService.convertToMain(periodToConvert.fiscalPeriodId);
      toast.success(result.message);
      await loadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل الدمج' : 'Failed to convert'));
    } finally {
      setConvertDialogOpen(false);
      setPeriodToConvert(null);
    }
  };

  const handleDeleteDbClick = (period: FiscalPeriodDto) => {
    setPeriodToDeleteDb(period);
    setDeleteDbDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDbConfirm = async () => {
    if (!periodToDeleteDb) return;
    try {
      const result = await fiscalPeriodsService.deleteSeparateDatabase(periodToDeleteDb.fiscalPeriodId);
      toast.success(result.message);
      await loadPeriods();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل الحذف' : 'Failed to delete database'));
    } finally {
      setDeleteDbDialogOpen(false);
      setPeriodToDeleteDb(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">{isRTL ? 'الفترات المالية' : 'Fiscal Periods'}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadPeriods} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setGenerateDialogOpen(true)}>
              {isRTL ? 'توليد فترات' : 'Generate'}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              {isRTL ? 'إضافة فترة' : 'Add Period'}
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{isRTL ? 'اسم الفترة' : 'Period Name'}</TableCell>
                <TableCell>{isRTL ? 'النوع' : 'Type'}</TableCell>
                <TableCell>{isRTL ? 'من تاريخ' : 'Start Date'}</TableCell>
                <TableCell>{isRTL ? 'إلى تاريخ' : 'End Date'}</TableCell>
                <TableCell>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                <TableCell align="center">{isRTL ? 'الإجراءات' : 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map((period) => (
                <TableRow key={period.fiscalPeriodId}>
                  <TableCell>{period.periodName}</TableCell>
                  <TableCell>
                    <Chip label={period.periodType} size="small"
                      color={period.periodType === 'Yearly' ? 'primary' : period.periodType === 'Quarterly' ? 'secondary' : 'default'} />
                  </TableCell>
                  <TableCell>{formatDate(period.startDate)}</TableCell>
                  <TableCell>{formatDate(period.endDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {period.isCurrent && <Chip label={isRTL ? 'حالية' : 'Current'} color="success" size="small" />}
                      {period.isClosed ? <Chip label={isRTL ? 'مغلقة' : 'Closed'} color="error" size="small" /> :
                        <Chip label={isRTL ? 'مفتوحة' : 'Open'} color="info" size="small" />}
                      {period.useSeparateDatabase && <Chip label={isRTL ? 'قاعدة منفصلة' : 'Separate DB'} color="warning" size="small" />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {!period.isClosed && (
                        <>
                          <IconButton size="small" color="primary" onClick={() => handleEdit(period)} title={isRTL ? 'تعديل' : 'Edit'}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {!period.isCurrent && (
                            <IconButton size="small" color="success" onClick={() => handleSetCurrent(period)} title={isRTL ? 'تعيين كحالية' : 'Set as Current'}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" color="warning" onClick={() => handleClose(period)} title={isRTL ? 'إغلاق' : 'Close'}>
                            <LockIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(period)} title={isRTL ? 'حذف' : 'Delete'}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {period.isClosed && (
                        <IconButton size="small" color="info" onClick={() => handleOpen(period)} title={isRTL ? 'فتح' : 'Open'}>
                          <LockOpenIcon fontSize="small" />
                        </IconButton>
                      )}
                      {period.useSeparateDatabase && (
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, period)} title={isRTL ? 'المزيد' : 'More'}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {periods.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">{isRTL ? 'لا توجد فترات مالية' : 'No fiscal periods found'}</Typography>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <FiscalPeriodDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSave} period={selectedPeriod} />
      <GeneratePeriodsDialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} onGenerate={handleGenerate} />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}</DialogTitle>
        <DialogContent>
          <Typography>
            {isRTL ? `هل أنت متأكد من حذف الفترة "${periodToDelete?.periodName}"؟` : `Are you sure you want to delete period "${periodToDelete?.periodName}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">{isRTL ? 'حذف' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Main Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>{isRTL ? 'تحويل إلى القاعدة الرئيسية' : 'Convert to Main Database'}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {isRTL ? 'سيتم نقل جميع البيانات من القاعدة المنفصلة إلى القاعدة الرئيسية ثم حذف القاعدة المنفصلة.' : 'All data will be moved from the separate database to the main database, then the separate database will be deleted.'}
          </Alert>
          <Typography>
            {isRTL ? `هل تريد دمج "${periodToConvert?.periodName}" في القاعدة الرئيسية؟` : `Do you want to merge "${periodToConvert?.periodName}" into the main database?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleConvertToMainConfirm} color="primary" variant="contained">{isRTL ? 'دمج' : 'Merge'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Database Dialog */}
      <Dialog open={deleteDbDialogOpen} onClose={() => setDeleteDbDialogOpen(false)}>
        <DialogTitle>{isRTL ? 'حذف قاعدة البيانات' : 'Delete Database'}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {isRTL ? 'سيتم حذف قاعدة البيانات المنفصلة نهائياً بدون نقل البيانات!' : 'The separate database will be permanently deleted without transferring data!'}
          </Alert>
          <Typography>
            {isRTL ? `هل أنت متأكد من حذف قاعدة "${periodToDeleteDb?.databaseName}"؟` : `Are you sure you want to delete database "${periodToDeleteDb?.databaseName}"?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDbDialogOpen(false)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleDeleteDbConfirm} color="error" variant="contained">{isRTL ? 'حذف' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* More Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {/* ❌ DISABLED: Merge غير آمن حالياً - يسبب تعارض في FiscalPeriodId
	<MenuItem onClick={() => menuPeriod && handleConvertToMainClick(menuPeriod)}>
  	  <ListItemIcon><TransformIcon fontSize="small" /></ListItemIcon>
  	  <ListItemText>{isRTL ? 'تحويل إلى القاعدة الرئيسية' : 'Convert to Main DB'}</ListItemText>
	</MenuItem>
	*/}
        <MenuItem onClick={() => menuPeriod && handleDeleteDbClick(menuPeriod)}>
          <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{isRTL ? 'حذف قاعدة البيانات' : 'Delete Database'}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FiscalPeriodsPage;